<template>
  <div class="app-container" :key="appKey">
    <el-config-provider>
      <el-container>
        <!-- Sidebar -->
        <el-aside width="240px" class="sidebar">
          <div class="sidebar-header">
            <h2>LTV Cocktail</h2>
          </div>
          <el-menu
            :router="true"
            class="sidebar-menu"
            background-color="#0c2732"
            text-color="#d8e1ea"
            active-text-color="#65c9c2"
          >
            <el-menu-item :index="`/${currentLocale}`">
              <el-icon><HomeFilled /></el-icon>
              <span>{{ t('menu.dashboard') }}</span>
            </el-menu-item>
            <el-menu-item :index="`/${currentLocale}/expenses`">
              <el-icon><Money /></el-icon>
              <span>{{ t('menu.expenses') }}</span>
            </el-menu-item>
            <el-menu-item :index="`/${currentLocale}/categories`">
              <el-icon><Folder /></el-icon>
              <span>{{ t('menu.categories') }}</span>
            </el-menu-item>
            <el-menu-item :index="`/${currentLocale}/report`">
              <el-icon><PieChart /></el-icon>
              <span>{{ t('menu.reports') }}</span>
            </el-menu-item>
            <el-menu-item index="/account-holders">
              <el-icon><UserFilled /></el-icon>
              <span>{{ t('menu.accountHolders') }}</span>
            </el-menu-item>
          </el-menu>
          
          <!-- User Profile & Language Switcher -->
          <div class="user-profile-container">
            <div class="user-profile">
              <div class="profile-info">
                <!-- Admin dropdown -->
                <el-dropdown trigger="click" class="admin-dropdown">
                  <div class="admin-area">
                    <el-avatar class="admin-avatar">A</el-avatar>
                    <div class="admin-details">
                      <span class="admin-name">Admin</span>
                      <span class="admin-role">Administrator</span>
                    </div>
                  </div>
                  <template #dropdown>
                    <el-dropdown-menu>
                      <el-dropdown-item>
                        {{ t('navbar.profile') }}
                      </el-dropdown-item>
                      <el-dropdown-item divided>
                        {{ t('navbar.logout') }}
                      </el-dropdown-item>
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>
                
                <!-- Language Globe -->
                <el-dropdown trigger="click" class="language-globe">
                  <div class="lang-indicator">
                    <div class="flag-circle" :class="{ 'is-loading': isLanguageChanging }">
                      <template v-if="!isLanguageChanging">
                        <span v-if="currentLocale === 'en'">🇺🇸</span>
                        <span v-else>🇻🇳</span>
                      </template>
                      <el-icon v-else class="is-loading"><Loading /></el-icon>
                    </div>
                  </div>
                  <template #dropdown>
                    <el-dropdown-menu>
                      <el-dropdown-item 
                        :class="{ active: currentLocale === 'en' }"
                        :disabled="isLanguageChanging"
                        @click="switchLanguage('en')"
                      >
                        <span class="flag-emoji">🇺🇸</span>
                        <span>English</span>
                      </el-dropdown-item>
                      <el-dropdown-item 
                        :class="{ active: currentLocale === 'vi' }"
                        :disabled="isLanguageChanging"
                        @click="switchLanguage('vi')"
                      >
                        <span class="flag-emoji">🇻🇳</span>
                        <span>Tiếng Việt</span>
                      </el-dropdown-item>
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>
              </div>
            </div>
          </div>
        </el-aside>
        
        <!-- Main Content -->
        <el-container class="main-container">
          <el-header class="header">
            <h3>{{ getPageTitle }}</h3>
          </el-header>
          
          <el-main>
            <router-view :key="currentLocale" />
          </el-main>
          
          <el-footer class="footer">
            <div>© {{ new Date().getFullYear() }} LTV Cocktail</div>
          </el-footer>
        </el-container>
      </el-container>
    </el-config-provider>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { HomeFilled, Money, Folder, PieChart, UserFilled, Loading } from '@element-plus/icons-vue';
import { useI18n } from 'vue-i18n';
import { LOCALES, setLocale } from './plugins/i18n/index';

const route = useRoute();
const { locale, t } = useI18n();

const currentLocale = computed(() => locale.value);
const isLanguageChanging = ref(false);
const appKey = ref(0); // Used to force entire app re-render

// Xử lý sự kiện thay đổi locale
const handleLocaleChanged = (event: Event) => {
  const customEvent = event as CustomEvent;
  const detail = customEvent.detail || {};
  
  if (detail.locale) {
    console.log(`App detected locale change to: ${detail.locale}`);
    // Chỉ increment key khi cần thiết để force re-render app
    appKey.value += 1;
  }
};

onMounted(() => {
  window.addEventListener('localeChanged', handleLocaleChanged);
});

onUnmounted(() => {
  window.removeEventListener('localeChanged', handleLocaleChanged);
});

const getPageTitle = computed(() => {
  const pathToI18nKey: Record<string, string> = {
    '/': 'dashboard.title',
    '/expenses': 'expenses.title',
    '/categories': 'categories.title',
    '/report': 'reports.title',
    '/account-holders': 'accountHolders.title'
  };
  
  // Xử lý route với locale prefix
  const path = route.path;
  // Loại bỏ locale prefix (ví dụ: /en/expenses -> /expenses)
  const pathWithoutLocale = path.replace(/^\/(?:en|vi)(?=\/|$)/, '');
  
  const i18nKey = pathToI18nKey[pathWithoutLocale] || pathToI18nKey[path];
  return i18nKey ? t(i18nKey) : 'Dashboard';
});

// Improved language switching function with visual feedback
function switchLanguage(newLocale: string) {
  if (isLanguageChanging.value) return;
  if (newLocale === currentLocale.value) return;
  
  console.log(`Switching language from ${currentLocale.value} to ${newLocale}`);
  isLanguageChanging.value = true;
  
  // Cập nhật URL với locale mới thay vì thay đổi locale trực tiếp
  // Điều này sẽ kích hoạt LocaleRouter, tránh việc set locale hai lần
  const currentPath = route.fullPath;
  const newPath = currentPath.replace(/^\/(en|vi)/, `/${newLocale}`);
  
  // Chỉ navigate nếu đường dẫn thực sự thay đổi
  if (newPath !== currentPath) {
    // Sử dụng ròuter.push thay vì setLocale trực tiếp
    // setLocale(newLocale as 'en' | 'vi'); 
    window.location.href = newPath;
  }
  
  // Reset changing status after a delay
  setTimeout(() => {
    isLanguageChanging.value = false;
  }, 1000);
}
</script>

<style>
.app-container {
  height: 100vh;
  width: 100vw;
  /* Bộ màu tối ưu điều chỉnh để bớt chói */
  --primary: #0e3547;             /* Màu chính tối hơn */
  --primary-dark: #092834;        /* Màu chính tối hơn */
  --primary-light: #164a63;       /* Màu chính nhạt hơn - tối hơn */
  --primary-ultra-light: #e6eef2; /* Màu chính siêu nhạt (nền) - ấm hơn */
  
  --accent: #37a59e;              /* Màu điểm nhấn - giảm độ sáng */
  --accent-light: #88c7c3;        /* Màu điểm nhấn nhạt - giảm độ sáng */
  
  --neutral-50: #f8fafc;          /* Nền sáng nhất - ấm hơn */
  --neutral-100: #f0f4f8;         /* Nền sáng - ấm hơn */
  --neutral-200: #e2e8f0;         /* Viền nhẹ, phân cách */
  --neutral-300: #cbd5e1;         /* Viền đậm */
  --neutral-400: #94a3b8;         /* Text phụ nhạt */
  --neutral-500: #64748b;         /* Text phụ */
  --neutral-600: #475569;         /* Text phụ đậm */
  --neutral-700: #334155;         /* Text chính */
  --neutral-800: #1e293b;         /* Text đậm */
  --neutral-900: #0f172a;         /* Text đậm nhất */
  
  --success: #0d9d71;             /* Thành công - tối hơn */
  --warning: #d97706;             /* Cảnh báo - tối hơn */
  --danger: #dc2626;              /* Nguy hiểm/Lỗi - tối hơn */
  --info: #2563eb;                /* Thông tin - tối hơn */
  
  --sidebar-bg: #0c2732;          /* Nền sidebar tối và ấm hơn */
  --sidebar-header-bg: #0a212a;   /* Nền header sidebar đậm hơn */
  --sidebar-text: #d8e1ea;        /* Màu text sidebar dịu hơn */
  --sidebar-text-hover: #ffffff;  /* Màu text khi hover */
  --sidebar-active: rgba(55, 165, 158, 0.12); /* Tăng độ hiện của màu active */
  --sidebar-border: #37a59e;      /* Màu viền khi active */
  --sidebar-icon: #65a9a5;        /* Màu icon dịu hơn */
  --sidebar-width: 240px;         /* Chiều rộng sidebar */
  
  --header-height: 64px;
  --footer-height: 40px;
  
  --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.03);
  --shadow-md: 0 2px 4px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.03);
  --shadow-lg: 0 4px 6px rgba(0, 0, 0, 0.05), 0 2px 4px rgba(0, 0, 0, 0.03);
}

/* Sidebar styles */
.sidebar {
  background-color: var(--sidebar-bg);
  color: var(--sidebar-text);
  width: var(--sidebar-width);
  height: 100vh;
  box-shadow: var(--shadow-md);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  border-right: 1px solid rgba(255, 255, 255, 0.03);
}

.sidebar-header {
  height: var(--header-height);
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  background-color: var(--sidebar-header-bg);
}

.sidebar-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: 0.5px;
  background: linear-gradient(90deg, #37a59e, #65c9c2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: none;
}

.sidebar-menu {
  border-right: none !important;
  height: calc(100vh - var(--header-height) - 100px);
  overflow-y: auto;
  padding-top: 12px;
}

.el-menu-item {
  display: flex;
  align-items: center;
  height: 48px;
  padding: 0 20px;
  margin: 6px 12px;
  border-radius: 8px;
  transition: all 0.25s ease;
}

.el-menu-item:hover {
  color: var(--sidebar-text-hover);
  background-color: rgba(255, 255, 255, 0.03) !important;
}

.el-menu-item.is-active {
  background-color: var(--sidebar-active) !important;
  border-left: 3px solid var(--sidebar-border);
}

.el-menu-item .el-icon {
  margin-right: 12px;
  font-size: 18px;
  color: var(--sidebar-icon);
  opacity: 0.9;
}

/* Header and content */
.header {
  background-color: #fff;
  box-shadow: var(--shadow-sm);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  height: var(--header-height);
  border-bottom: 1px solid var(--neutral-200);
}

.header h3 {
  font-size: 18px;
  font-weight: 600;
  color: var(--neutral-800);
  margin: 0;
}

.main-container {
  height: 100vh;
  overflow: hidden;
  background-color: var(--neutral-50);
}

.el-main {
  padding: 24px;
  overflow-y: auto;
  color: var(--neutral-700);
}

.footer {
  height: var(--footer-height);
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fff;
  color: var(--neutral-500);
  font-size: 14px;
  border-top: 1px solid var(--neutral-200);
}

/* User Profile styles */
.user-profile-container {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 16px;
  background-color: var(--sidebar-header-bg);
  backdrop-filter: blur(6px);
  border-top: 1px solid rgba(255, 255, 255, 0.04);
  display: flex;
  flex-direction: column;
}

.user-profile {
  display: flex;
  flex-direction: column;
}

.profile-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
}

/* Admin dropdown */
.admin-dropdown {
  flex: 1;
}

.admin-area {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 8px;
  transition: all 0.25s ease;
  cursor: pointer;
}

.admin-area:hover {
  background-color: rgba(22, 74, 99, 0.3);
}

.admin-avatar {
  background: linear-gradient(135deg, var(--accent), var(--accent-light));
  color: var(--primary-dark);
  font-weight: bold;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.1);
}

.admin-details {
  display: flex;
  flex-direction: column;
  margin-left: 12px;
}

.admin-name {
  color: #fff;
  font-weight: 500;
  font-size: 14px;
}

.admin-role {
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
}

.el-dropdown-menu .active {
  background-color: var(--primary-ultra-light);
  color: var(--primary);
  font-weight: 500;
}

/* Language selector */
.language-globe {
  cursor: pointer;
  margin-right: 20px;
}

.lang-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
}

.flag-circle {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: var(--primary-dark);
  border-radius: 0;
  transition: all 0.25s ease;
  font-size: 24px;
  box-shadow: none;
  position: relative;
}

.flag-circle:hover {
  transform: scale(1.1);
  box-shadow: none;
}

.flag-circle.is-loading {
  animation: pulse 1.5s infinite;
}

.flag-circle .is-loading {
  animation: spin 1s infinite linear;
  font-size: 14px;
  color: #fff;
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.7; }
  100% { opacity: 1; }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.flag-emoji {
  font-size: 20px;
  margin-right: 8px;
  display: inline-block;
  border-radius: 0;
}

/* Scrollbar styling */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: var(--neutral-300);
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: var(--neutral-400);
}

/* Card styles for consistent content */
.el-card {
  border-radius: 8px;
  border: none;
  box-shadow: var(--shadow-sm) !important;
  transition: box-shadow 0.25s ease;
  background-color: #fff;
}

.el-card:hover {
  box-shadow: var(--shadow-md) !important;
}

.el-card__header {
  border-bottom: 1px solid var(--neutral-200);
  padding: 16px 20px;
  font-weight: 500;
  color: var(--neutral-800);
  background-color: rgba(240, 244, 248, 0.5);
}

.el-card__body {
  padding: 20px;
  color: var(--neutral-700);
}

/* Button styles */
.el-button--primary {
  background-color: var(--primary);
  border-color: var(--primary);
  color: white;
}

.el-button--primary:hover, 
.el-button--primary:focus {
  background-color: var(--primary-dark);
  border-color: var(--primary-dark);
}

.el-button--success {
  background-color: var(--success);
  border-color: var(--success);
}

.el-button--info {
  background-color: var(--info);
  border-color: var(--info);
}

.el-button--warning {
  background-color: var(--warning);
  border-color: var(--warning);
}

.el-button--danger {
  background-color: var(--danger);
  border-color: var(--danger);
}

/* Form controls with reduced contrast */
.el-input__inner {
  border-color: var(--neutral-300);
  color: var(--neutral-700);
}

.el-input__inner:focus {
  border-color: var(--accent);
}

/* Table styles */
.el-table th {
  background-color: var(--primary-ultra-light);
  color: var(--primary);
  font-weight: 500;
}

.el-table tr {
  background-color: #fff;
  transition: background-color 0.2s ease;
}

.el-table tr:hover {
  background-color: var(--neutral-100);
}

.el-table td {
  color: var(--neutral-700);
}

/* Adjust el-menu background color via inline style since it's defined in component */
.el-menu {
  background-color: var(--sidebar-bg) !important;
}
</style>
